import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { BehaviorSubject, EMPTY, ReplaySubject, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, pairwise, takeUntil, tap } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable, selectors } from 'app/core/state';
import { isInDev } from 'app/core/utilities/env-util';
import { formatUserName } from 'app/core/utilities/user-util';
import { AppState, Interval, Timeout } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import assertNever from 'app/utils/assert-never';
import { HelpCenterUrls } from 'app/utils/help-center';
import { Loggable } from 'app/utils/logging/loggable';
import { filterCodecs } from 'app/utils/media/rtc';
import { DUMMY_VIDEO_STREAM } from 'app/utils/media/stream';
import { isUndefined, notNil } from 'app/utils/stream-util';
import Company from 'entity/Company';
import Social from 'entity/Social';
import User from 'entity/User';
import SocialPlatformType from 'enums/SocialPlatformType';
import {
  IncomingMessageType,
  TrackStateChangeMessage,
  TrackStateMessage,
  PublishOfferMessage,
  OutgoingMessageType,
  PublishCandidateMessage,
  PeerCandidate,
  ParticipantState,
  BaseUser,
  SocialEndType,
  IncomingMessage,
  PublishBitrateMessage,
  SvcSettings,
} from 'messages/websocket.messages';

import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from '../modal-simple-social/modal-simple-social.component';
import { MyParticipant } from '../preciate-stream/my-participant';
import { formatPeerStats } from '../preciate-stream/peer-connection-stats-helper';
import PreciateEventHelper from '../preciate-stream/preciate-event-helper';
import { Room } from '../preciate-stream/room';
import { MediaStreamWrapper, RoomParticipant } from '../preciate-stream/room-participant';
import { ReconnectionStateType } from '../preciate-stream/types';
import { PeerStats } from '../preciate-stream/types';
import { socialAppActions, socialAppSelectors } from '../state/app';
import { mediaSelectors } from '../state/media';
import { RESOLUTION_SMALL, ScalabilityMode, VideoCodec, VideoCodecMimeType, VideoCodecType } from '../types/media';

import { ComputePressureService, PressureState } from './compute-pressure/compute.pressure.service';
import { DeviceService } from './device/device.service';
import { PublisherType, RtcStatsStreamService, StatsCounter } from './rtc-stats-stream';
import { SceneService } from './scene.service';
import { SocialMessageService } from './social-message.service';
import { findNewSubscriptions, findRemovedSubscriptions, getLogComplement } from './subscription/subscription.helper';
import { SubscriptionService } from './subscription/subscription.service';
import { SubscriberState } from './subscription/subscription.types';
import { INITIAL_PEER_STATS } from './video-room/video-room.types';
import { WebsocketService } from './websocket.service';

// Maps userId to participants media stream
export type ParticipantMediaStreams = Record<string, MediaStreamWrapper>;

@Injectable({
  providedIn: 'root',
})
export class VideoRoomService extends Loggable {
  private messageService = inject(SocialMessageService);
  private deviceService = inject(DeviceService);
  public websocketService = inject(WebsocketService);
  private store = inject<Store<AppState>>(Store);
  private subscriptionService = inject(SubscriptionService);
  private dialog = inject(MatDialog);
  private sceneService = inject(SceneService);
  public rtcStatsStreamService = inject(RtcStatsStreamService);

  private socialEnded = new Subject<SocialEndType>();
  private user?: User;
  private company?: Company;
  private senderVideoTrack?: RTCRtpSender;
  private senderAudioTrack?: RTCRtpSender;
  private rtcConfig?: RTCConfiguration;
  private peerTime?: number;
  private remoteCandidates: RTCIceCandidate[] = [];
  private watchRtcApiKey?: string;
  private isTestUser = false;
  private peerConnectionTime?: number; // Track peer connection start time (unix milliseconds)
  private reconnectionState = ReconnectionStateType.INITIAL;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private isMicEnabled = true;
  private isVideoEnabled = true;
  private publisherCandidates: PublishCandidateMessage[] = [];
  private myParticipant?: MyParticipant;
  private cameraStream: MediaStream = DUMMY_VIDEO_STREAM;
  private audioStream: MediaStream | null = null;
  private social?: Social;
  private scalabilityMode = ScalabilityMode.L1T2;
  private videoCodecOverride?: VideoCodec;
  private videoStreams: ParticipantMediaStreams = {};
  private audioStreams: ParticipantMediaStreams = {};
  private participantVideoStreamSource = new BehaviorSubject<ParticipantMediaStreams>({});
  private participantAudioStreamSource = new BehaviorSubject<ParticipantMediaStreams>({});
  private svcSettings?: SvcSettings;
  private isWatchRtcFlagEnabled = false;
  public peerStats?: PeerStats;
  public statsPeer?: RTCPeerConnection;
  private statsInterval?: Timeout;
  private _peer?: RTCPeerConnection;

  public activeRoom?: Room;
  public didAddParticipantAudio = new Subject<RoomParticipant>();
  public didAddParticipantVideo = new Subject<RoomParticipant>();
  public didSubscribeParticipant = new Subject<PreciateEventHelper>();
  public didUnsubscribeParticipant = new Subject<PreciateEventHelper>();
  public participantAudioStatusChanged = new Subject<{ enabled: boolean; userId: string }>();
  public participants: Map<string, RoomParticipant> = new Map<string, RoomParticipant>();
  public participantVideoStatusChanged = new Subject<{ enabled: boolean; userId: string }>();
  public peerConnectionFormattedStats?: RTCStatsReport[];
  public publisherAudio?: HTMLAudioElement = document.createElement('audio'); // holds the logged in persons audio element
  public publisherVideo?: HTMLVideoElement = undefined; // holds the logged in persons video element
  public slowNetworkConnection = new BehaviorSubject<boolean>(false);
  public socialEnded$ = this.socialEnded.asObservable();
  public subscriptions = new Map<string, boolean>();
  public participantVideoStream$ = this.participantVideoStreamSource.asObservable();
  public participantAudioStream$ = this.participantAudioStreamSource.asObservable();
  public mixStream?: MediaStream;

  /* protocols to notify changes within transmissions */
  public participantConnected = new Subject<RoomParticipant>();
  public participantDisconnected = new Subject<RoomParticipant>();

  public subscribers: SubscriberState[] = [];

  private publishVideoBitrate = RESOLUTION_SMALL.bitrate;
  private hdPresenterFlag = false;

  public SVC_PERFORMANCE_CHECK_INTERVAL = 10000;
  public connectionStatusBadCondition = 'poor';

  private scalableVideoCoding = false;
  private svcInterval?: Interval;
  private svcStatsBadCondition = false;
  private pressureState: PressureState = PressureState.NOMINAL;
  private computePressureService?: ComputePressureService;

  public constructor() {
    super();

    this.setDisplayName('VideoRoomService');

    this.deviceService.cameraStream$
      .pipe(
        filter(notNil),
        catchError((error: unknown) => {
          this.error('Error with video stream', error);
          return EMPTY;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((stream) => {
        this.cameraStream = stream ?? DUMMY_VIDEO_STREAM;
        this.replaceVideoTrack(this.cameraStream.getVideoTracks()[0]).catch((e) =>
          this.error('Error replacing video track', e),
        );
      });

    this.deviceService.audioStream$
      .pipe(
        filter(notNil),
        catchError((error: unknown) => {
          this.error('Error with audio stream', error);
          return EMPTY;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((stream) => {
        this.audioStream = stream ?? null;
        if (stream) {
          this.replaceAudioTrack(stream.getAudioTracks()[0]).catch((e) => this.error('Error replacing audio track', e));
        }
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.hd_presenter)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hd_presenter) => {
        this.hdPresenterFlag = hd_presenter;
      });
  }

  public init(): void {
    this.info(`Initializing video room service`);
    // ! FIXME: destroyed needs to be re-instantiated when initializing the service,
    // ! otherwise it will be stopped and subscriptions won't trigger.
    this.destroyed$ = new ReplaySubject(1);

    this.store
      .select(selectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ isTestUser, user }) => {
        this.isTestUser = isTestUser;
        this.user = user;
      });

    this.store
      .select(selectors.getCompany)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((company) => {
        this.company = company;
      });

    this.store
      .select(coreSelectors.getConfig)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      // FIXME: This is a hack to get the watchRtc working now that we're out of staging. Remove this prefix from the server
      .subscribe(({ watchRtcApiKey }) => (this.watchRtcApiKey = watchRtcApiKey?.replace(/staging:/, '')));

    this.store
      .select(socialAppSelectors.getCurrentSocial)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((social) => (this.social = social));

    this.store
      .select(mediaSelectors.isVideoEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => (this.isVideoEnabled = enabled));
    this.store
      .select(mediaSelectors.isMicEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => (this.isMicEnabled = enabled));

    // Participant state
    this.store
      .select(socialAppSelectors.getJoinedUser)
      .pipe(
        filter(notNil),
        map((user) => user.userId),
        filter(notNil),
        takeUntil(this.destroyed$),
      )
      .subscribe((userId) => this.setLocalParticipant(new MyParticipant(userId, this, this.store)));

    this.store
      .select(socialAppSelectors.getPublishVideoBitrate)
      .pipe(
        distinctUntilChanged(),
        filter(() => this.peerTime !== undefined),
        tap((bitrate) => (this.publishVideoBitrate = bitrate)),
        takeUntil(this.destroyed$),
      )
      .subscribe((bitrate: number) => {
        this.updatePublishBitrate(bitrate);
      });

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(pairwise(), takeUntil(this.destroyed$))
      .subscribe(([previousIsPresenting, isPresenting]) => {
        if (previousIsPresenting !== isPresenting) {
          const scalabilityMode = isPresenting
            ? this.svcSettings?.hdScalabilityMode
            : this.svcSettings?.scalabilityMode;
          if (scalabilityMode) {
            this.setScalabilityMode(this.toScalabilityMode(scalabilityMode));
          }
        }
      });

    this.websocketService.message$.pipe(takeUntil(this.destroyed$)).subscribe(this.handleWebsocketMessage);

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_watchrtc)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isWatchRTCFlagEnabled) => {
        this.isWatchRtcFlagEnabled = isWatchRTCFlagEnabled;

        // if (this.isWatchRtcFlagEnabled) {
        //   try {
        //     watchRTC.init();

        //     let lastWatchRTCUpdate: number | undefined = undefined;
        //     watchRTC.addStateListener((state) => {
        //       this.info('watchRTC state', state);
        //     });
        //     watchRTC.addStatsListener((stats) => {
        //       this.info('watchRTC stats', stats);
        //       const now = Date.now();
        //       if (lastWatchRTCUpdate && now - lastWatchRTCUpdate > 10000) {
        //         this.error('watchRTC error, disconnecting', now, lastWatchRTCUpdate, now - lastWatchRTCUpdate);
        //         watchRTC.disableDataCollection();
        //         watchRTC.disconnect();
        //         watchRTC.addStateListener(null);
        //         watchRTC.addStatsListener(null);
        //         return;
        //       }
        //       lastWatchRTCUpdate = now;
        //     });
        //   } catch (e) {
        //     this.error('Error initializing watchRTC', e);
        //   }
        // }
      });

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => {
        if (isPresentationActive) {
          this.muteAudio();
        } else {
          this.unmuteAudio();
        }
      });

    this.subscriptionService.subscription$.pipe(takeUntil(this.destroyed$)).subscribe((subs) => {
      // Check if the subscription has an existing room participant
      const filteredSubscriptions: SubscriberState[] = subs.filter((sub) => {
        return this.participants.has(sub.id);
      });

      const newSubscriptions = findNewSubscriptions(this.subscribers, filteredSubscriptions);
      const removedSubscriptions = findRemovedSubscriptions(this.subscribers, filteredSubscriptions);

      // Filter out any subscriptions that exist in both the old and new
      const updateSubscriptions = newSubscriptions.filter((currentObj) =>
        removedSubscriptions.find((prevObj) => prevObj.id === currentObj.id),
      );

      // Find objects that are only in the new array
      const filteredNewSubscriptions = newSubscriptions.filter(
        (currentObj) => !removedSubscriptions.find((prevObj) => prevObj.id === currentObj.id),
      );

      // Find objects that are only in the removed array
      const filteredRemovedSubscriptions = removedSubscriptions.filter(
        (prevObj) => !newSubscriptions.find((currentObj) => currentObj.id === prevObj.id),
      );

      // If the current participant map is empty
      // Return and set subscribers  = []
      if (this.participants.size === 0) {
        this.subscribers = [];
        return;
      }

      this.subscribeUsers(filteredNewSubscriptions);
      this.unsubscribeUsers(filteredRemovedSubscriptions);
      this.updateSubscriptionReason(updateSubscriptions);

      this.subscribers = filteredSubscriptions;
    });
    // Initialize Peer stats
    this.statsPeer = undefined;
    this.peerStats = { ...INITIAL_PEER_STATS };

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.scalable_video_coding)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((scalable_video_coding) => {
        this.scalableVideoCoding = scalable_video_coding;
        if (scalable_video_coding) {
          this.setSVCCheckInterval(this.SVC_PERFORMANCE_CHECK_INTERVAL);
          this.computePressureService = new ComputePressureService();
          this.computePressureService.observePressure((state: PressureState) => {
            this.pressureState = state;
            this.handlePressureChange(state);
          });
        } else if (this.svcInterval) {
          clearInterval(this.svcInterval);
        }
      });
  }

  private handlePressureChange(state: PressureState): void {
    this.pressureState = state;
  }

  //Allow debugger to set value
  public setSVCCheckInterval(value: number): void {
    this.SVC_PERFORMANCE_CHECK_INTERVAL = value;
    if (this.svcInterval) {
      clearInterval(this.svcInterval);
    }
    this.svcInterval = setInterval(
      this.checkPerformanceAndConnectivity.bind(this),
      this.SVC_PERFORMANCE_CHECK_INTERVAL,
    );
  }

  public setSVCParameters(connectionStatusBadCondition: string, svcInterval: number): void {
    this.connectionStatusBadCondition = connectionStatusBadCondition;
    this.setSVCCheckInterval(svcInterval);
  }

  public muteAudio(): void {
    if (this.publisherAudio) {
      this.publisherAudio.muted = true;
    }
  }

  public unmuteAudio(): void {
    if (this.publisherAudio) {
      this.publisherAudio.muted = false;
    }
  }

  public unsubscribeUsers(subscribers: SubscriberState[]): void {
    subscribers.forEach((subscriber) => {
      const { id } = subscriber;
      const participant = this.getParticipant(id) as RoomParticipant;
      if (!participant) {
        return;
      }

      const participantName = formatUserName(participant.user);
      this.info(`Unsubscribing from ${participantName}`);

      participant.setMediaNeeded({ audio: false, video: false });
      participant.setReason(undefined);
      // TODO: this is thowing a server error. Remove?
      // participant.reconfigure();
      // if (this.isUserPresenting || this.sceneService.currentZoom <= 0.6) {
      //   participant.quickUnSubscription();
      // } else {
      participant.scheduleUnsubscription();
      // }
    });
  }

  private subscribeUsers(subscribers: SubscriberState[]): void {
    subscribers.forEach((subscriber) => {
      const { audioRequired, id, reason, videoRequired } = subscriber;
      const participant = this.getParticipant(id) as RoomParticipant;
      if (!participant) {
        this.warn('There is currently no participant for this subscriber');
        return;
      }

      const participantName = formatUserName(participant.user);
      participant.setReason(reason);
      if (participant.videoNeeded === videoRequired && participant.audioNeeded === audioRequired) {
        return;
      }

      participant.setMediaNeeded({ audio: audioRequired, video: videoRequired });

      if (participant.connection) {
        this.info(`Reconfiguring ${participantName} after changes. ${getLogComplement(subscriber)}`);
        participant.reconfigure();
      } else {
        this.info(`Subscribing to ${participantName} ${getLogComplement(subscriber)}`);
        participant.subscribe();
      }
    });
  }

  // Update reason of subscription of existing subscribers
  // Do not unsubscribe and resubscribe to existing subscriptions
  private updateSubscriptionReason(subscribers: SubscriberState[]): void {
    subscribers.forEach((sub) => {
      const { id, reason } = sub;
      const participant = this.getParticipant(id) as RoomParticipant;
      this.info(`Updating reason for ${participant.user?.userId}`, { reason });
      participant.reconfigure();
      participant.setReason(reason);
    });
  }

  public reSubscribeSVC(
    subscribers: SubscriberState[],
    temporalLayer: number,
    spatialLayer: number,
    svc: boolean,
  ): void {
    subscribers.forEach((subscriber) => {
      const participant = this.getParticipant(subscriber.id) as RoomParticipant;
      participant.selectTemporalAndSpatialLayer(temporalLayer, spatialLayer, svc);
    });
  }

  public setSocial(social: Social): void {
    this.social = social;
  }

  public setLocalParticipant(myParticipant: MyParticipant): void {
    this.myParticipant = myParticipant;
  }

  public getParticipant(userId: string): RoomParticipant | undefined {
    return this.participants.get(userId);
  }

  private removeParticipant(userId: string): void {
    if (!userId) {
      return this.warn('No userId to remove participant');
    }
    const participant = this.getParticipant(userId) as RoomParticipant;
    if (participant) {
      this.info('Removing participant', participant.toString());
    }
    participant?.destroy();
    this.participants.delete(userId);
    this.store.dispatch(socialAppActions.REMOVE_PARTICIPANT({ userId }));
  }

  public clear(): void {
    this.publisherCandidates = [];

    this.info('Clearing all participants');

    this.participants.forEach((participant) => participant.destroy());
    this.participants.clear();

    this.store.dispatch(socialAppActions.REMOVE_ALL_PARTICIPANTS());
  }

  public destroy(): void {
    this.info('Destroying video room service');

    this.reconnectionState = ReconnectionStateType.INITIAL;

    if (this.svcInterval) {
      clearInterval(this.svcInterval);
    }

    this.cameraStream = DUMMY_VIDEO_STREAM;
    this.audioStream = null;

    this.myParticipant?.destroy();

    this.peer?.close();
    this.peer = undefined;
    this.peerTime = undefined;

    this.activeRoom = undefined;
    this.myParticipant = undefined;

    this.clear();

    this.social = undefined;
    this.publisherAudio = undefined;
    this.publisherVideo = undefined;

    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.senderAudioTrack = undefined;
    this.senderVideoTrack = undefined;
    this.subscribers = [];
  }

  public getReconnectionState(): ReconnectionStateType {
    return this.reconnectionState;
  }

  private get isWatchRTCEnabled(): boolean {
    return !this.isTestUser && this.isWatchRtcFlagEnabled;
  }
  /**
   * Create a RTCConfiguration object with all connection mechanisms considered from previous connection failures, and
   * watchrtc configuration applied.
   *
   * @param preferredRTCConfig Override the service's RTCConfiguration with another.
   * @return A constructed configuration with the proper reconnection mode applied.
   */
  public createRtcConfig(preferredRTCConfig?: RTCConfiguration): RTCConfiguration {
    let relay = false;
    let forceTcp = false;

    // Prefer the passed in configuration to the one supplied by VideoRoomService
    const config = preferredRTCConfig ?? this.rtcConfig;

    switch (this.reconnectionState) {
      case ReconnectionStateType.FORCE_RELAY:
        relay = true;
        break;

      case ReconnectionStateType.FORCE_TCP:
        relay = true;
        forceTcp = true;
        break;

      case ReconnectionStateType.INITIAL:
        break;

      default:
        assertNever(this.reconnectionState);
    }

    // Construct watchrtc api config
    const { watchRtcApiKey: rtcApiKey } = this;
    const { sessionId, socialId } = this.social || {};
    const { company, user } = this;
    const keys = {
      env: isInDev() ? 'dev' : 'prod',
      companyId: company?.companyId ?? 'unknown',
    };

    const watchrtc =
      rtcApiKey && this.isWatchRTCEnabled
        ? {
            rtcApiKey,
            rtcRoomId: `${socialId}:${sessionId}`,
            rtcPeerId: `${user?.userId ?? 'unknown'}:${user?.firstName ?? 'unknown'}`,
            keys,
          }
        : {};

    let { iceServers } = config || { iceServers: [] };

    // If forcing TCP, remove any entries with a UDP transport param
    if (forceTcp) {
      iceServers = iceServers?.filter(({ urls }) => {
        const urlArray = Array.isArray(urls) ? urls : [urls];
        return !urlArray.map((u) => new URL(u)).some((u) => u.searchParams.get('transport') === 'udp');
      });
    }

    // Remove turn servers for test users
    if (this.isTestUser) {
      iceServers = iceServers?.filter(({ urls }) => {
        const urlArray = Array.isArray(urls) ? urls : [urls];
        return !urlArray.some((u) => u.startsWith('turn'));
      });
    }

    const rtcConfig = {
      ...config,
      iceTransportPolicy: relay ? 'relay' : undefined,
      iceServers,
      watchrtc,
    } as unknown as RTCConfiguration; // Cast through unknown to allow watchrtc property

    return rtcConfig;
  }

  public createRoom(): Promise<Room | undefined> {
    const video = document.createElement('video');
    if (!this.myParticipant) {
      this.fatal('Tried to create room with undefined participant');
      throw new Error('Tried to create room with undefined participant');
    }
    this.activeRoom = new Room(this.myParticipant);
    video.srcObject = this.cameraStream;
    video.width = 220;
    video.height = 220;
    video.muted = true;

    this.publisherVideo = video;
    if (!this.publisherAudio) {
      this.publisherAudio = document.createElement('audio');
    }
    this.publisherAudio.srcObject = this.audioStream;

    return new Promise((resolve): void => {
      // On safari, video.oncanplay will not fire for the dummy video stream
      if (DUMMY_VIDEO_STREAM.id === this.cameraStream.id) {
        resolve(this.activeRoom);
      }

      // Test users may not have video to play. Resolve the promise and move on
      if (this.isTestUser) {
        resolve(this.activeRoom);
      }

      video.oncanplay = () => {
        video.oncanplay = null;
        resolve(this.activeRoom);
      };
    });
  }

  public advanceReconnectionState({
    connectionTime,
    isLocalPeer = false,
  }: {
    connectionTime?: number;
    isLocalPeer?: boolean;
  }): void {
    const failedTime = Date.now();
    const delta = connectionTime ? failedTime - connectionTime : undefined;
    const connectionTiming = { connectionTime, failedTime, delta };

    switch (this.reconnectionState) {
      case ReconnectionStateType.INITIAL:
        this.warn('A peer connection failed. Changing reconnection mode to FORCE_RELAY', connectionTiming);
        this.reconnectionState = ReconnectionStateType.FORCE_RELAY;
        break;
      case ReconnectionStateType.FORCE_RELAY:
        this.warn('A peer connection failed. Changing reconnection mode to FORCE_TCP', connectionTiming);
        this.reconnectionState = ReconnectionStateType.FORCE_TCP;
        break;
      case ReconnectionStateType.FORCE_TCP:
        this.fatal('A peer connection failed and was not able to recover.');
        if (isLocalPeer) {
          this.showNotRecoveringPeerModal();
        }
        break;
      default:
        assertNever(this.reconnectionState);
    }
  }

  public showNotRecoveringPeerModal(): void {
    const data: ModalSimpleSocialData = {
      modalTitle: `Failed publishing your media`,
      text: 'Your media failed to publish and other users in the room cannot see or hear you. This may be related to a VPN or Network Security settings. Please try to join the room without being on a VPN. If the issue persists, please review this help center article or reach out to <a href="mailto:support@scoot.app" target="_blank">support@scoot.app</a>',
      primaryButtonText: 'Help Center',
    };
    this.dialog
      .open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
        panelClass: 'social-modal',
        data,
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        if (result === 'primary') {
          const url = HelpCenterUrls.SCOOT_PREPARE_NETWORK;
          window.open(url, '_blank')?.focus();
        }
      });
  }

  private async handleConnectionFailed(): Promise<void> {
    const oneMinuteAgo = moment().subtract(1, 'minute');
    const connectionTime = this.peerConnectionTime;

    this.rtcStatsStreamService.incrementCounter(StatsCounter.PARTICIPANT_PUBLISHER_CONNECTION_FAILED);

    // If the connection fails in under a minute, or we never connected, advance the reconnection state machine to
    // potentially more reliable methods
    if (!connectionTime || connectionTime > oneMinuteAgo.valueOf()) {
      this.advanceReconnectionState({ connectionTime, isLocalPeer: true });
    }

    await this.sendRtcStats('failed');
    await this.republish();
  }

  private async createPublishPeer(): Promise<void> {
    if (!this.audioStream) {
      return;
    }

    const config = this.createRtcConfig();
    const peerTime = Date.now();
    this.peerTime = peerTime;

    this.info('Creating publish peer connection', { config, peerTime });

    const peer = new RTCPeerConnection(config);

    peer.onconnectionstatechange = async () => {
      const { connectionState } = peer;
      this.store.dispatch(socialAppActions.PUBLISH_STATE_CHANGE({ connectionState }));
      this.info('Publish state change', { connectionState });
      switch (connectionState) {
        case 'connected':
          {
            // Track the time this connection started to identify when connections fail quickly.
            this.peerConnectionTime = Date.now();

            this.updatePublishBitrate(this.publishVideoBitrate);

            await this.sendRtcStats(connectionState);
          }
          break;

        case 'failed':
          await this.handleConnectionFailed();
          break;

        default:
          break;
      }
    };

    peer.oniceconnectionstatechange = (): void => {
      const { iceConnectionState } = peer;

      this.info('Publish ICE state change', { iceConnectionState, peerTime });
    };

    peer.onicegatheringstatechange = (): void => {
      const { iceGatheringState } = this.peer as RTCPeerConnection;
      this.info('Publish ICE gathering state change', { iceGatheringState, peerTime });
    };

    this.peer = peer;
    peer.onicecandidate = (event) => this.publishCandidate(event, peerTime);
    peer.onicecandidateerror = (e) => this.trace('Candidate error', e);

    // Video
    this.configureVideoPublishing();

    // Audio
    this.configureAudioPublishing();

    const isScoot = this.social?.platform === SocialPlatformType.SCOOT;
    if (isScoot) {
      peer.ontrack = (event) => (this.mixStream = event.streams[0]);
    }
    const transceivers = this.peer?.getTransceivers();
    transceivers.forEach((t) => {
      const track = t.sender.track;
      if (track?.kind !== 'audio' || !isScoot) {
        t.direction = 'sendonly';
      }
    });
    const offer = await this.peer?.createOffer();
    await this.peer?.setLocalDescription(offer);
    this.sendOffer(offer, peerTime);
  }

  private configureVideoPublishing(): void {
    const videoTracks = this.cameraStream.getVideoTracks();
    const videoTrack = videoTracks[0];
    this.myParticipant?.setVideoTrack(videoTrack);

    const notify = false;
    if (this.isVideoEnabled) {
      this.myParticipant?.enableVideo(notify);
    } else {
      this.myParticipant?.disableVideo(notify);
    }

    let codecs: RTCRtpCodecCapability[] | undefined;
    // Not all browsers support static getCapabilities. Firefox does not.
    if (RTCRtpReceiver.getCapabilities) {
      codecs = RTCRtpReceiver.getCapabilities('video')?.codecs;

      if (this.videoCodecOverride) {
        const { mimeType, sdpFormatFilter } = this.videoCodecOverride;
        const formatFilter = sdpFormatFilter !== '' ? sdpFormatFilter : undefined;
        codecs = isUndefined(codecs) ? undefined : filterCodecs(codecs, mimeType, formatFilter);
      }
    }

    const sendEncodings: RTCRtpEncodingParameters[] | undefined =
      this.scalabilityMode === ScalabilityMode.NONE
        ? undefined
        : [{ scalabilityMode: this.scalabilityMode } as RTCRtpEncodingParameters];

    this.info('Publishing video', { codecs, sendEncodings });

    const transceiver = this.peer?.addTransceiver(videoTrack, {
      direction: 'sendonly',
      sendEncodings,
    });

    if (codecs && transceiver && transceiver.setCodecPreferences) {
      transceiver.setCodecPreferences(codecs);
    }
    this.senderVideoTrack = transceiver?.sender;
  }

  private configureAudioPublishing(): void {
    if (!this.audioStream) {
      this.info('User has no audio stream, not adding audio to publish peer');
      return;
    }
    const audioTracks = this.audioStream.getAudioTracks();
    this.myParticipant?.setAudioTrack(audioTracks[0]);

    const notify = false;
    if (this.isMicEnabled) {
      this.myParticipant?.enableAudio(notify);
    } else {
      this.myParticipant?.disableAudio(notify);
    }

    this.senderAudioTrack = this.peer?.addTrack(audioTracks[0]);
  }

  private sendOffer(offer: RTCSessionDescriptionInit, peerTime: number): void {
    const { sdp, type } = offer;
    if (!type || !sdp) {
      this.error('Got unexpected undefined value in offer', offer);
      return;
    }

    const msg: PublishOfferMessage = {
      t: OutgoingMessageType.PUBOFF,
      offer: { type, sdp },
      pt: peerTime,
    };
    this.websocketService.sendMessage(msg);
    this.publisherCandidates.forEach((c) => {
      this.websocketService.sendMessage(c);
    });
    this.publisherCandidates = [];
  }

  private async replaceVideoTrack(track: MediaStreamTrack): Promise<void> {
    if (!this.myParticipant) {
      return new Promise(() => undefined);
    }

    this.myParticipant.cleanVideoTracks();
    this.myParticipant.setVideoTrack(track);
    await this.senderVideoTrack?.replaceTrack(track);
  }

  private replaceAudioTrack(track: MediaStreamTrack): Promise<void> {
    if (!this.myParticipant) {
      return new Promise(() => undefined);
    }
    const promise = new Promise<void>(async (resolve) => {
      if (!this.senderAudioTrack) {
        setTimeout(() => resolve(this.replaceAudioTrack(track)), 200);
      } else {
        // Clean Audio Track
        this.myParticipant?.cleanAudioTracks();
        // Set Audio and Enable/Disable based on media state
        this.myParticipant?.setAudioTrack(track);
        // Notify track to send OutgoingMessageType
        const notify = true;
        if (this.isMicEnabled) {
          this.myParticipant?.enableAudio(notify);
        } else {
          this.myParticipant?.disableAudio(notify);
        }
        await this.senderAudioTrack.replaceTrack(track);
        resolve();
      }
    });
    return promise;
  }

  /**
   * Republish to the video server.
   *
   * WARNING: This should not be used outside of the debugger or internally to VideoRoomService.
   */
  public async republish(): Promise<void> {
    this.info('Publisher: republishing peer');
    this.closePublishPeer();
    await this.createPublishPeer();
  }

  /**
   * Close the publish peer.
   *
   * WARNING: This should not be used outside of the debugger or internally to VideoRoomService.
   */
  public closePublishPeer(): void {
    this.peer?.close();
    this.peer = undefined;
  }

  private participantConnecting({
    camera,
    mic,
    publishPeerTime,
    user,
    x,
    y,
  }: {
    user: BaseUser;
    x: number;
    y: number;
    publishPeerTime: number;
    camera: boolean;
    mic: boolean;
  }): RoomParticipant | undefined {
    const { userId } = user;

    let participant = this.participants.get(userId) as RoomParticipant;
    if (!participant) {
      participant = new RoomParticipant(
        this,
        { user, x, y, publishPeerTime, camera, mic },
        this.store,
        this.sceneService,
        this.rtcStatsStreamService,
      );
      this.participants.set(userId, participant as RoomParticipant);
      this.store.dispatch(socialAppActions.ADD_PARTICIPANT({ userId, camera, mic }));
    }

    this.participantConnected.next(participant);
    return participant;
  }

  private participantLeft(userId: string): void {
    const participant = this.getParticipant(userId) as RoomParticipant;
    const player = this.sceneService.getPlayer(userId);
    player?.enableMic(false);
    this.store.dispatch(
      socialAppActions.PARTICIPANT_AUDIO_MUTE_CHANGE({
        userId,
        muted: true,
      }),
    );
    if (player) {
      if (this.sceneService.sceneManager?.groupManager) {
        this.sceneService.sceneManager.groupManager.removeParticipant(player);
      }
    } else {
      this.warn('Player not found for userId', userId);
    }
    if (participant) {
      this.participantDisconnected.next(participant);
      this.removeParticipant(participant.userId);
    }
  }

  private registerScootaverseConnection({ user, userId }: { user: BaseUser; userId: string }): void {
    this.store.dispatch(socialAppActions.ADD_SCOOTAVERSE_USER({ userId, user }));
  }

  private deleteScootaverseConnection({ userId }: { userId: string }): void {
    this.store.dispatch(socialAppActions.REMOVE_SCOOTAVERSE_USER({ userId }));
  }

  public handleInitStateMsg(participants: ParticipantState[]): void {
    this.participants.forEach(({ userId }) => {
      const removed = !participants.find((participant) => participant.user.userId === userId);
      if (removed) {
        this.removeParticipant(userId);
      }
    });
    participants.forEach((p) => {
      if (!this.participants.has(p.user.userId)) {
        this.participantConnecting(p);
      }
    });
  }

  private handleParticipantList(participants: ParticipantState[]): void {
    const listed = new Set();

    participants.forEach((p) => {
      const { userId } = p.user;
      assertDefined(userId);
      listed.add(userId);
      const participant = this.participants.get(userId) as RoomParticipant;
      if (!participant) {
        this.participantConnecting(p);
      }
    });

    for (const id in this.participants) {
      if (!listed.has(id)) {
        this.participantLeft(id);
      }
    }
  }

  public removeParticipants(): void {
    this.participants.forEach((participant) => {
      this.participantDisconnected.next(participant);
    });
    this.participants.clear();
  }

  private videoOnEvent(userId: string): void {
    const participant = this.participants.get(userId) as RoomParticipant;
    participant?.videoOnEvent();
  }

  private videoOffEvent(userId: string): void {
    const participant = this.participants.get(userId) as RoomParticipant;
    participant?.videoOffEvent();
  }

  public sendToAll(msg: TrackStateChangeMessage): void {
    this.websocketService.sendMessage(msg);
  }

  private handleWebsocketMessage = async (msg: IncomingMessage): Promise<void> => {
    const { t: type } = msg;
    switch (type) {
      case IncomingMessageType.INIT_STATE: {
        this.handleInitStateMsg(msg.participants);
        return;
      }

      case IncomingMessageType.DATA: {
        this.messageService.receiveMessage(msg.d);
        return;
      }

      case IncomingMessageType.CHATMSG: {
        this.messageService.receiveChatMessage(msg);
        return;
      }

      case IncomingMessageType.CHATRES: {
        this.messageService.receiveChatMessage(msg);
        return;
      }

      case IncomingMessageType.CHATHIST: {
        this.messageService.receiveChatMessage(msg);
        return;
      }

      case IncomingMessageType.E: {
        this.eventMessage(msg);
        return;
      }

      case IncomingMessageType.PUBANS: {
        const publishAnswerMessage = msg;
        if (publishAnswerMessage.pt === this.peerTime) {
          await this.peer
            ?.setRemoteDescription(publishAnswerMessage.d as RTCSessionDescriptionInit)
            .then(() => this.addRemoteCandidates());
        } else {
          this.error('Publish answer peer time mismatch', this.peerTime, publishAnswerMessage.pt);
        }
        return;
      }

      case IncomingMessageType.PUBCAN: {
        const publishServerCandidateMessage = msg;
        this.addRemoteCandidate(publishServerCandidateMessage.d, publishServerCandidateMessage.pt);
        return;
      }

      case IncomingMessageType.CON: {
        this.participantConnecting(msg);
        this.registerScootaverseConnection(msg);
        return;
      }

      //Message for users connecting in another room
      case IncomingMessageType.USER: {
        this.registerScootaverseConnection(msg);
        return;
      }

      case IncomingMessageType.LEFT: {
        this.participantLeft(msg.userId);
        this.deleteScootaverseConnection(msg);
        return;
      }

      //Message for users connecting in another room
      case IncomingMessageType.USER_LEFT: {
        this.deleteScootaverseConnection(msg);
        return;
      }

      case IncomingMessageType.PUBLISH: {
        const svc = msg.svc;
        this.svcSettings = svc;
        if (svc) {
          this.scalabilityMode = this.toScalabilityMode(svc.scalabilityMode);
        }
        await this.republish();
        return;
      }

      case IncomingMessageType.PARTICIPANTS: {
        this.handleParticipantList(msg.d);
        return;
      }

      case IncomingMessageType.SLOW: {
        this.slowNetworkConnection.next(msg.slow);
        return;
      }

      case IncomingMessageType.FR:
        return;

      case IncomingMessageType.RTCCFG: {
        this.rtcConfig = JSON.parse(msg.d);
        this.info('RTC Config Called: ', this.rtcConfig);
        return;
      }

      case IncomingMessageType.ENDED: {
        this.socialEnded.next(msg.reason);
        return;
      }

      case IncomingMessageType.ERROR: {
        this.error('Websocket error message', msg.code, msg.error);
        return;
      }

      case IncomingMessageType.PUBLISHING_STARTED: {
        const participant = this.participants.get(msg.userId) as RoomParticipant;
        if (participant) {
          participant.publishingStarted(msg);
        }
        return;
      }

      case IncomingMessageType.TU_AUDIO: {
        if (this.isTestUser) {
          // FIXME: Should this use media service?
          // Notify track to send OutgoingMessageType
          const notify = true;
          if (msg.muted) {
            this.myParticipant?.disableAudio(notify);
          } else {
            this.myParticipant?.enableAudio(notify);
          }
        }
        return;
      }
    }
  };

  private eventMessage(msg: TrackStateMessage): void {
    const trackEnabled = msg.type === 'on';
    switch (msg.kind) {
      case 'video': {
        if (trackEnabled) {
          this.videoOnEvent(msg.userId);
        } else {
          this.videoOffEvent(msg.userId);
        }

        // Notify others
        this.participantVideoStatusChanged.next({
          enabled: trackEnabled,
          userId: msg.userId,
        });
        break;
      }
      case 'audio': {
        const participant = this.participants.get(msg.userId) as RoomParticipant;
        if (participant) {
          participant.participantToggleAudio(trackEnabled);
        }

        this.participantAudioStatusChanged.next({
          enabled: trackEnabled,
          userId: msg.userId,
        });
        break;
      }
    }
  }

  private publishCandidate(event: RTCPeerConnectionIceEvent, peerTime: number): void {
    const { candidate } = event;
    if (!candidate) {
      this.info('ICE Candidate was null, done');
      return;
    }
    if (!candidate.sdpMid) {
      this.error('publishCandidate: candidate sdpMid was unexpectedly null', candidate);
      return;
    }

    this.info('ICE Candidate received', candidate.toJSON());
    const msg: PublishCandidateMessage = {
      t: OutgoingMessageType.PUBCAN,
      d: candidate as PeerCandidate,
      pt: peerTime,
    };
    if (this.websocketService.isConnected) {
      this.websocketService.sendMessage(msg);
    } else {
      this.publisherCandidates.push(msg);
    }
  }

  private addRemoteCandidate(d: RTCIceCandidateInit, pt: number): void {
    if (pt === this.peerTime) {
      const candidate = new RTCIceCandidate(d);
      if (this.peer?.remoteDescription) {
        this.peer?.addIceCandidate(candidate).catch((e) => this.error('Publish candidate failed', candidate, e));
      } else {
        this.remoteCandidates.push(candidate);
      }
    } else {
      this.error('Publish candidate peer time mismatch', this.peerTime, pt);
    }
  }

  private addRemoteCandidates(): void {
    this.remoteCandidates.forEach((candidate) => {
      this.peer?.addIceCandidate(candidate).catch((e) => this.error('Publish candidate error', candidate, e));
    });
    this.remoteCandidates = [];
  }

  private async sendRtcStats(state: string): Promise<void> {
    if (!this.peer) {
      this.warn('No peer to send stats', state);
      return;
    }
    const stats = await this.peer?.getStats();
    if (stats) {
      const rtcReports: RTCStatsReport[] = [];
      stats.forEach((report: RTCStatsReport) => rtcReports.push(report));
      this.info('Publish RTC Stats ', rtcReports, state);
    } else {
      this.info('No publish RTC stats', state);
    }
  }

  private updatePublishBitrate(bitrate: number): void {
    // Only send bitrate updates if the hd presenter feature flag is enabled
    if (!this.hdPresenterFlag) {
      return;
    }

    if (!this.peerTime) {
      this.warn('Peer time not set, cannot update publish bitrate');
      return;
    }

    const pubBitrate: PublishBitrateMessage = {
      t: OutgoingMessageType.PUB_BITRATE,
      bitrate,
      peerTime: this.peerTime,
    };
    this.websocketService.sendMessage(pubBitrate);
  }

  private toScalabilityMode(scalabilityMode?: string): ScalabilityMode {
    if (scalabilityMode) {
      return ScalabilityMode[scalabilityMode as keyof typeof ScalabilityMode] ?? ScalabilityMode.NONE;
    }
    return ScalabilityMode.NONE;
  }

  /**
   * Indicate that a participant is publishing a new video stream
   * @param userId The userId of the participant that is publishing a new stream
   * @param stream A stream that is being published, or undefined if the participant is not publishing
   */
  public participantVideoStreamChanged(userId: string, stream: MediaStreamWrapper | undefined) {
    if (stream) {
      this.videoStreams[userId] = stream;
    } else {
      delete this.videoStreams[userId];
    }
    this.participantVideoStreamSource.next(this.videoStreams);
  }

  /**
   * Indicate that a participant is publishing a new audio stream
   * @param userId The userId of the participant that is publishing a new stream
   * @param stream A stream that is being published, or undefined if the participant is not publishing
   */
  public participantAudioStreamChanged(userId: string, stream: MediaStreamWrapper | undefined) {
    if (stream) {
      this.audioStreams[userId] = stream;
    } else {
      delete this.audioStreams[userId];
    }
    this.participantAudioStreamSource.next(this.audioStreams);
  }

  public removeSubscription(userId: string): void {
    this.subscriptions.delete(userId);
  }

  /**
   * Get and Format stats for Peer connection debugger
   * @param id The userId of the participant that is publishing a new stream
   * @param interval The interval at which the stats are being retrieved
   */
  public startPeerConnectionStats(caller: string, interval: number, id?: string): void {
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
    }
    this.statsInterval = setInterval(async () => {
      if (id) {
        this.statsPeer = this.getParticipant(id)?.connection;
        if (!this.statsPeer) {
          this.error('Participant not found for stats', caller, id);
          return;
        }
        const stats = await this.statsPeer?.getStats();
        if (stats && this.peerStats) {
          this.peerStats = formatPeerStats(stats, this.peerStats);
        }
      }

      if (!id) {
        this.statsPeer = this.peer;
        const stats = await this.peer?.getStats();
        if (stats && this.peerStats) {
          this.peerStats = formatPeerStats(stats, this.peerStats);
        }
      }
    }, interval);
  }

  public stopPeerConnectionStats(): void {
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
    }
    this.statsPeer = undefined;
    this.peerStats = { ...INITIAL_PEER_STATS };
  }

  public async getSnapshotOfPeerConnectionStats(id?: string): Promise<PeerStats | undefined> {
    let peer = this.peer;
    if (id) {
      peer = this.getParticipant(id)?.connection;
    }
    const stats = await peer?.getStats();
    if (!stats) {
      return;
    }
    const peerStats = formatPeerStats(stats);
    return peerStats;
  }

  public async simulatePeerMediaFailure(): Promise<void> {
    this.reconnectionState = ReconnectionStateType.FORCE_TCP;
    await this.handleConnectionFailed();
  }

  public get peer(): RTCPeerConnection | undefined {
    return this._peer;
  }

  private set peer(newPeer: RTCPeerConnection | undefined) {
    if (newPeer) {
      this.rtcStatsStreamService.addPublishMonitoring(PublisherType.PARTICIPANT, newPeer);
    } else {
      this.rtcStatsStreamService.removePublishMonitoring(PublisherType.PARTICIPANT);
    }
    this._peer = newPeer;
  }

  public selectTemporalLayer(userId: string, layer: number): void {
    const participant = this.getParticipant(userId) as RoomParticipant;
    if (!participant) {
      this.error('selectTemporalLayer: Participant not found', userId);
      return;
    }
    participant.selectTemporalLayer(layer);
  }

  public selectSpatialLayer(userId: string, layer: number): void {
    const participant = this.getParticipant(userId) as RoomParticipant;
    if (!participant) {
      this.error('selectSpatialLayer: Participant not found', userId);
      return;
    }
    participant.selectSpatialLayer(layer);
  }

  public getSVCLayerSelection(userId: string): { spatialLayer: number; temporalLayer: number } {
    const participant = this.getParticipant(userId) as RoomParticipant;
    if (!participant) {
      this.error('selectSpatialLayer: Participant not found', userId);
      throw new Error('Participant not found');
    }

    return {
      spatialLayer: participant.spatialLayer,
      temporalLayer: participant.temporalLayer,
    };
  }

  // #region Test Utilities

  /**
   * Simulate peer close
   * @param userId Close the peer for this userId
   */
  public closeSubscriberPeer(userId: string): void {
    const subscriber = this.participants.get(userId);
    if (!subscriber) {
      this.warn('Subscriber peer not found', userId);
      return;
    }
    subscriber.forceClosePeer();
  }

  public async setCodecOverride(codecType: VideoCodecType): Promise<void> {
    switch (codecType) {
      case VideoCodecType.VP8:
        this.videoCodecOverride = {
          mimeType: VideoCodecMimeType.VP8,
          sdpFormatFilter: '',
        };
        break;
      case VideoCodecType.VP9:
        this.videoCodecOverride = {
          mimeType: VideoCodecMimeType.VP9,
          sdpFormatFilter: '',
        };
        break;
      case VideoCodecType.H264:
        this.videoCodecOverride = {
          mimeType: VideoCodecMimeType.H264,
          sdpFormatFilter: 'profile-level-id=42e01f',
        };
        break;
      case VideoCodecType.AV1:
        this.videoCodecOverride = {
          mimeType: VideoCodecMimeType.AV1,
          sdpFormatFilter: '',
        };
        break;
    }
    // Skip republishing if we don't have a peer
    if (!this.peer) {
      return;
    }
    await this.republish();
  }

  public setScalabilityMode(mode: ScalabilityMode): void {
    this.scalabilityMode = mode;
    this.peer?.getSenders().forEach((sender) => {
      if (sender.track?.kind === 'video') {
        const params = sender.getParameters();
        if (mode === ScalabilityMode.NONE) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          delete (params.encodings[0] as any).scalabilityMode;
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (params.encodings[0] as any).scalabilityMode = mode;
        }
        void sender.setParameters(params);
        this.info('Scalability mode changed to', mode);
      }
    });
  }

  public getSvcSettings(): SvcSettings | undefined {
    return this.svcSettings;
  }

  private getNetworkConditions(): string {
    // Check if the Network Information API is supported
    if ('connection' in navigator) {
      // Get the effective type of the connection
      // @ts-expect-error Connection attribute isn't explicit in navigator object, but it exists
      switch (navigator.connection.effectiveType) {
        case '4g':
          return 'good';
        case '3g':
          return 'average';
        default:
          return 'poor';
      }
    } else {
      // If the Network Information API is not supported, return 'unknown'
      return 'unknown';
    }
  }

  public checkPerformanceAndConnectivity(): void {
    if (this.scalableVideoCoding) {
      const actualConnectionState = this.getNetworkConditions();
      if (!this.svcStatsBadCondition) {
        if (this.pressureState === PressureState.CRITICAL) {
          this.info('CPU usage is high --- FPS: ', this.pressureState);
          this.setSVCStatsBadCondition();
        } else if (actualConnectionState === this.connectionStatusBadCondition) {
          this.info('Connection is poor');
          this.setSVCStatsBadCondition();
        }
      } else {
        if (
          this.pressureState !== PressureState.CRITICAL &&
          actualConnectionState !== this.connectionStatusBadCondition
        ) {
          this.info('CPU usage or connection status solved');
          this.svcStatsBadCondition = false;
          this.reSubscribeSVC(this.subscribers, 1, 2, false);
        }
      }
    }
  }

  private setSVCStatsBadCondition() {
    this.svcStatsBadCondition = true;
    this.reSubscribeSVC(this.subscribers, 0, 0, true);
  }

  /**
   * Returns users ring color
   * @param userId return ring's color for this userId
   */
  public getPresentersRingColor(userId: string): string | undefined {
    const userPlayerObject = this.sceneService.getPlayer(userId);
    return userPlayerObject?.ringColor;
  }

  // #endregion
}
